import { EmergencyConnectionPoint } from 'features/emergency/enums';

export interface EmergencyAutoConnectDecisionArgs {
  callId?: number;
  emergencyConnectionPoint?: EmergencyConnectionPoint;
  emergencyPhoneNumber: string;
  isAcceptingUser: boolean;
  isAddressVerificationNeeded: boolean;
  isAutoConnectAttempted: boolean;
  isEmergencyAutoConnectBlockedByTeamingSync: boolean;
  isEmergencyHearingNotDisconnected: boolean;
  isLoggedOutEmergencyCall: boolean;
}

export interface EmergencyAutoConnectDecision {
  shouldAttemptAutoConnect: boolean;
  shouldMarkAutoConnectAttempted: boolean;
  shouldTurnOffSpawningCall: boolean;
  logMessage: string;
}

const createSkipDecision = (
  logMessage: string,
  {
    shouldMarkAutoConnectAttempted = false,
    shouldTurnOffSpawningCall = false,
  }: Partial<
    Pick<
      EmergencyAutoConnectDecision,
      'shouldMarkAutoConnectAttempted' | 'shouldTurnOffSpawningCall'
    >
  > = {}
): EmergencyAutoConnectDecision => ({
  shouldAttemptAutoConnect: false,
  shouldMarkAutoConnectAttempted,
  shouldTurnOffSpawningCall,
  logMessage,
});

export const getEmergencyAutoConnectDecision = ({
  callId,
  emergencyConnectionPoint,
  emergencyPhoneNumber,
  isAcceptingUser,
  isAddressVerificationNeeded,
  isAutoConnectAttempted,
  isEmergencyAutoConnectBlockedByTeamingSync,
  isEmergencyHearingNotDisconnected,
  isLoggedOutEmergencyCall,
}: EmergencyAutoConnectDecisionArgs): EmergencyAutoConnectDecision => {
  if (!isLoggedOutEmergencyCall) {
    if (!callId) {
      return createSkipDecision(
        'Skipping, no call ID and not a logged-out call',
        {
          shouldTurnOffSpawningCall: true,
        }
      );
    }

    if (isAddressVerificationNeeded) {
      return createSkipDecision(
        'Skipping, address verification needed and not a logged-out call',
        {
          shouldTurnOffSpawningCall: true,
        }
      );
    }

    if (isAcceptingUser) {
      return createSkipDecision(
        'Skipping, user is accepting and not a logged-out call',
        {
          shouldTurnOffSpawningCall: true,
        }
      );
    }

    if (isEmergencyAutoConnectBlockedByTeamingSync) {
      return createSkipDecision(
        'Skipping, emergency auto-connect blocked by teaming ownership and not a logged-out call',
        {
          shouldTurnOffSpawningCall: true,
        }
      );
    }

    if (emergencyConnectionPoint === EmergencyConnectionPoint.OTHER) {
      return createSkipDecision(
        'Skipping, emergency connection point is OTHER and not a logged-out call',
        {
          shouldTurnOffSpawningCall: true,
        }
      );
    }
  }

  if (isAutoConnectAttempted) {
    return createSkipDecision('Skipping, already attempted');
  }

  if (!emergencyPhoneNumber) {
    return createSkipDecision('Skipping, no emergency phone number');
  }

  if (isEmergencyHearingNotDisconnected) {
    return createSkipDecision(
      'Skipping, already connected or not disconnected',
      {
        shouldMarkAutoConnectAttempted: true,
      }
    );
  }

  return {
    shouldAttemptAutoConnect: true,
    shouldMarkAutoConnectAttempted: true,
    shouldTurnOffSpawningCall: false,
    logMessage: 'Attempting to connect',
  };
};
